import { FormEvent, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Camera, LocateFixed, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { httpServices as services } from "@/services";
import { executeOrQueue } from "@/offline/executeOrQueue";
import { queueMediaUpload } from "@/offline/outbox";
import { useOffline } from "@/offline/OfflineProvider";
import { toApiId } from "@/services/http/mappers";
import type { FarmDetail, VisitActivityDefinition, VisitReason } from "@/types/domain";
import { captureCurrentPosition, saveFieldVisitContext } from "@/features/visits/fieldVisitLocal";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";

export default function UnscheduledVisitStartPage() {
  const navigate = useNavigate();
  const offline = useOffline();
  const [farms, setFarms] = useState<FarmDetail[]>([]);
  const [reasons, setReasons] = useState<VisitReason[]>([]);
  const [activityCatalogue, setActivityCatalogue] = useState<VisitActivityDefinition[]>([]);
  const [farmerId, setFarmerId] = useState("");
  const [farmId, setFarmId] = useState("");
  const [plotIds, setPlotIds] = useState<string[]>([]);
  const [reasonIds, setReasonIds] = useState<string[]>([]);
  const [unscheduledReason, setUnscheduledReason] = useState("");
  const [observations, setObservations] = useState("");
  const [checkInPhoto, setCheckInPhoto] = useState<File>();
  const [error, setError] = useState("");
  const [working, setWorking] = useState(false);
  const [gpsMessage, setGpsMessage] = useState("GPS will be captured at check-in.");

  useEffect(() => {
    let active = true;
    Promise.all([
      services.farms.list({ pageSize: 100 }),
      services.visits.listReasons(),
      services.visits.listActivityCatalogue(),
    ])
      .then(([farmResult, reasonRows, activityRows]) => {
        if (!active) return;
        setFarms(farmResult.items);
        setReasons(reasonRows);
        setActivityCatalogue(activityRows);
      })
      .catch((reason: Error) => {
        if (active) setError(reason.message || "Official Visit reference data could not be loaded.");
      });
    return () => { active = false; };
  }, []);

  const farmers = useMemo(() => {
    const map = new Map<string, string>();
    farms.filter((farm) => farm.farmStatus === "ACTIVE").forEach((farm) => {
      if (!map.has(farm.farmerId)) map.set(farm.farmerId, farm.farmerName);
    });
    return Array.from(map, ([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [farms]);

  const farmerFarms = useMemo(
    () => farms.filter((farm) => farm.farmerId === farmerId && farm.farmStatus === "ACTIVE"),
    [farms, farmerId],
  );
  const selectedFarm = farmerFarms.find((farm) => farm.id === farmId);
  const activePlots = (selectedFarm?.plotsData || []).filter((plot) => plot.status === "ACTIVE");

  const selectFarmer = (id: string) => {
    const candidates = farms.filter((farm) => farm.farmerId === id && farm.farmStatus === "ACTIVE");
    const onlyFarm = candidates.length === 1 ? candidates[0] : undefined;
    setFarmerId(id);
    setFarmId(onlyFarm?.id || "");
    setPlotIds([]);
  };

  const togglePlot = (id: string, checked: boolean) => {
    setPlotIds((current) => checked ? Array.from(new Set([...current, id])) : current.filter((value) => value !== id));
  };

  const toggleReason = (id: string, checked: boolean) => {
    setReasonIds((current) => checked ? Array.from(new Set([...current, id])) : current.filter((value) => value !== id));
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!farmerId || !farmId) {
      toast.error("Choose the farmer and Farm.");
      return;
    }
    if (plotIds.length === 0) {
      toast.error("Select at least one Plot being visited.");
      return;
    }
    if (reasonIds.length === 0) {
      toast.error("Select at least one reason for the visit.");
      return;
    }
    if (!unscheduledReason.trim()) {
      toast.error("Explain why this visit was not part of a sanctioned Visit Plan.");
      return;
    }
    if (!checkInPhoto) {
      toast.error("A check-in photo is required.");
      return;
    }

    setWorking(true);
    try {
      setGpsMessage("Capturing precise GPS position...");
      const position = await captureCurrentPosition();
      setGpsMessage(`GPS captured · ±${Math.round(position.accuracyMetres)} m`);

      const visitId = crypto.randomUUID();
      const evidenceId = crypto.randomUUID();
      const startedAt = new Date().toISOString();
      const isOffline = offline.simulatedOffline || !offline.online;
      const farmer = farmers.find((item) => item.id === farmerId);
      const selectedPlots = activePlots.filter((plot) => plotIds.includes(plot.id));
      const selectedReasons = reasons.filter((reason) => reasonIds.includes(reason.id));

      saveFieldVisitContext({
        visitId,
        farmerId,
        farmerName: farmer?.name || "Farmer",
        farmId,
        farmName: selectedFarm?.name || "Farm",
        plotIds,
        plotNames: selectedPlots.map((plot) => plot.name),
        reasonIds,
        reasonNames: selectedReasons.map((reason) => reason.reasonName),
        activityCatalogue,
        startedAt,
        latitude: position.latitude,
        longitude: position.longitude,
        gpsAccuracyMetres: position.accuracyMetres,
        evidenceTypes: ["CHECK_IN"],
        activityCount: 0,
        followUpCount: 0,
        observations: observations.trim() || undefined,
      });

      const apiPayload = {
        client_generated_id: visitId,
        farmer_id: toApiId(farmerId),
        farm_id: toApiId(farmId),
        plot_ids: plotIds.map(toApiId),
        reason_ids: reasonIds.map(toApiId),
        unscheduled_reason: unscheduledReason.trim(),
        started_at: startedAt,
        latitude: position.latitude,
        longitude: position.longitude,
        gps_accuracy_metres: position.accuracyMetres,
        device_identifier: offline.deviceId,
        created_offline: isOffline,
        local_created_at: startedAt,
        observations: observations.trim() || undefined,
      };

      const result = await executeOrQueue({
        offline: isOffline,
        request: {
          entityType: "FIELD_VISIT_UNSCHEDULED_START",
          entityId: visitId,
          method: "POST",
          path: "/visits/unscheduled/start",
          payload: apiPayload,
        },
        execute: () => services.visits.startUnscheduled({
          clientGeneratedId: visitId,
          farmerId,
          farmId,
          plotIds,
          reasonIds,
          unscheduledReason: unscheduledReason.trim(),
          startedAt,
          latitude: position.latitude,
          longitude: position.longitude,
          gpsAccuracyMetres: position.accuracyMetres,
          deviceIdentifier: offline.deviceId,
          createdOffline: false,
          localCreatedAt: startedAt,
          observations: observations.trim() || undefined,
        }, visitId),
      });

      const capturedAt = new Date().toISOString();
      const evidenceQuery = {
        captured_at: capturedAt,
        latitude: position.latitude,
        longitude: position.longitude,
        gps_accuracy_metres: position.accuracyMetres,
        plot_id: plotIds[0] ? toApiId(plotIds[0]) : undefined,
        device_identifier: offline.deviceId,
        created_offline: isOffline,
        local_created_at: capturedAt,
        client_generated_id: evidenceId,
      };

      if (result.mode === "QUEUED") {
        await queueMediaUpload({
          id: evidenceId,
          entityType: "FIELD_VISIT_EVIDENCE",
          entityId: visitId,
          path: `/visits/${toApiId(visitId)}/evidence/CHECK_IN`,
          file: checkInPhoto,
          query: evidenceQuery,
        });
      } else {
        try {
          await services.visits.uploadEvidence(visitId, {
            clientGeneratedId: evidenceId,
            evidenceType: "CHECK_IN",
            file: checkInPhoto,
            capturedAt,
            latitude: position.latitude,
            longitude: position.longitude,
            gpsAccuracyMetres: position.accuracyMetres,
            plotId: plotIds[0],
            deviceIdentifier: offline.deviceId,
            createdOffline: false,
            localCreatedAt: capturedAt,
          });
        } catch (uploadError) {
          if (!navigator.onLine) {
            await queueMediaUpload({
              id: evidenceId,
              entityType: "FIELD_VISIT_EVIDENCE",
              entityId: visitId,
              path: `/visits/${toApiId(visitId)}/evidence/CHECK_IN`,
              file: checkInPhoto,
              query: evidenceQuery,
            });
          } else {
            throw uploadError;
          }
        }
      }

      toast.success(result.mode === "QUEUED" ? "Official visit checked in offline" : "Official visit started", {
        description: "The visit is identified as UNSCHEDULED and retains the management reason, GPS and evidence.",
      });
      navigate(`/visits/${visitId}/execute`);
    } catch (reason) {
      const message = reason instanceof Error ? reason.message : "Official Visit could not be started.";
      setGpsMessage(message);
      toast.error(message);
    } finally {
      setWorking(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="unscheduled-visit-start-page">
      <PageHeader
        eyebrow="Field Visits · Management"
        title="Official Unscheduled Visit"
        description="For authorized supervisory, administration or director field work that was not part of a sanctioned Visit Plan."
        backTo="/visits"
      />

      {error && <Card className="border-destructive/30"><CardContent className="p-4 text-sm text-destructive">{error}</CardContent></Card>}

      <Card className="border-warning/30 bg-warning/[0.025]">
        <CardContent className="flex items-start gap-3 p-4 text-sm">
          <ShieldCheck className="mt-0.5 size-5 text-warning" />
          <div>
            <p className="font-semibold">Management-controlled exception</p>
            <p className="mt-1 text-muted-foreground">The backend only permits this workflow for users with field-assignment/management authority. The reason is permanently recorded.</p>
          </div>
        </CardContent>
      </Card>

      <form onSubmit={submit} className="space-y-5">
        <section className="form-section">
          <h2 className="text-lg font-semibold">Farmer, Farm & Plot</h2>
          <div className="mt-5 grid gap-5 md:grid-cols-2">
            <div className="space-y-2">
              <Label>Farmer</Label>
              <Select value={farmerId} onValueChange={selectFarmer}>
                <SelectTrigger><SelectValue placeholder="Select farmer" /></SelectTrigger>
                <SelectContent>{farmers.map((farmer) => <SelectItem key={farmer.id} value={farmer.id}>{farmer.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Farm</Label>
              <Select value={farmId} onValueChange={(value: string) => { setFarmId(value); setPlotIds([]); }} disabled={!farmerId}>
                <SelectTrigger><SelectValue placeholder="Select Farm" /></SelectTrigger>
                <SelectContent>{farmerFarms.map((farm) => <SelectItem key={farm.id} value={farm.id}>{farm.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>

          <div className="mt-5 grid gap-2 sm:grid-cols-2">
            {activePlots.map((plot) => <label key={plot.id} className="flex cursor-pointer items-start gap-3 rounded-xl border p-3"><input type="checkbox" className="mt-1 size-4" checked={plotIds.includes(plot.id)} onChange={(event: React.ChangeEvent<HTMLInputElement>) => togglePlot(plot.id, event.target.checked)} /><span><span className="font-medium">{plot.name}</span><span className="block text-xs text-muted-foreground">{plot.code}{plot.localityName ? ` · ${plot.localityName}` : ""}</span></span></label>)}
          </div>
        </section>

        <section className="form-section">
          <h2 className="text-lg font-semibold">Purpose & exception reason</h2>
          <div className="mt-4 grid gap-2 sm:grid-cols-2">
            {reasons.map((reason) => <label key={reason.id} className="flex cursor-pointer items-start gap-3 rounded-xl border p-3"><input type="checkbox" className="mt-1 size-4" checked={reasonIds.includes(reason.id)} onChange={(event: React.ChangeEvent<HTMLInputElement>) => toggleReason(reason.id, event.target.checked)} /><span><span className="font-medium">{reason.reasonName}</span>{reason.description && <span className="block text-xs text-muted-foreground">{reason.description}</span>}</span></label>)}
          </div>
          <div className="mt-5 space-y-2">
            <Label htmlFor="unscheduled-reason">Why is this visit unscheduled?</Label>
            <Textarea id="unscheduled-reason" value={unscheduledReason} onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => setUnscheduledReason(event.target.value)} placeholder="Management/supervisory reason for visiting outside a sanctioned agenda" />
          </div>
          <div className="mt-4 space-y-2">
            <Label htmlFor="unscheduled-note">Initial note</Label>
            <Textarea id="unscheduled-note" value={observations} onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => setObservations(event.target.value)} placeholder="Optional note on arrival" />
          </div>
        </section>

        <Card className={!checkInPhoto ? "border-warning/30" : "border-primary/20"}>
          <CardContent className="p-5">
            <div className="flex items-start gap-3">
              <Camera className="mt-0.5 size-5 text-primary" />
              <div className="flex-1">
                <p className="font-semibold">Mandatory check-in photo</p>
                <p className="mt-1 text-sm text-muted-foreground">A fresh photo, GPS and timestamp are required just like a planned field visit.</p>
                <Input className="mt-4" type="file" accept="image/jpeg,image/png,image/webp" capture="environment" onChange={(event: React.ChangeEvent<HTMLInputElement>) => setCheckInPhoto(event.target.files?.[0])} />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="flex flex-col gap-4 p-5 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-start gap-3"><LocateFixed className="mt-0.5 size-5 text-primary" /><div><p className="font-semibold">GPS check-in</p><p className="text-sm text-muted-foreground">{gpsMessage}</p></div></div>
            <Button type="submit" disabled={working || !!error}>{working ? <LocateFixed className="mr-2 size-4 animate-pulse" /> : <ShieldCheck className="mr-2 size-4" />}{working ? "Checking in..." : "Start official visit"}</Button>
          </CardContent>
        </Card>
      </form>
    </div>
  );
}

